import type { EntityVisual, Lane, ObstacleKind, PickupKind, PowerupKind } from './types'

/* ---------------------------------------------------------------- road ---- */

export const LANES: readonly Lane[] = [0, 1, 2]
/** Metres between lane centres. */
export const LANE_WIDTH = 3.4
/** Lateral position of each lane centre, metres from the road axis. */
export const LANE_X: readonly number[] = [-LANE_WIDTH, 0, LANE_WIDTH]
/** Half width of the tractor unit, metres. */
export const TRUCK_HALF_WIDTH = 1.25
/** Length of the truck's collision box, metres. */
export const TRUCK_DEPTH = 9

/* --------------------------------------------------------------- motion --- */

/** m/s at the start of a run. */
export const SPEED_START = 42
/** m/s ceiling — how fast the world actually moves. */
export const SPEED_MAX = 100
/** m/s gained per second of survival: top speed lands at ~53 s. */
export const SPEED_RAMP = 1.1
/**
 * The truck drives faster than a truck plausibly can, so the speedometer is
 * geared down to the original readout: ~80 km/h off the line, 209 km/h flat
 * out. Simulation speed and displayed speed are deliberately separate.
 */
export const SPEEDO_SCALE = 209 / (SPEED_MAX * 3.6)
/** m/s → km/h as shown to the player. */
export const speedo = (metresPerSecond: number): number => metresPerSecond * 3.6 * SPEEDO_SCALE
/** Seconds a lane change takes. */
export const LANE_CHANGE_TIME = 0.16

/* --------------------------------------------------------------- spawns --- */

/**
 * Metres ahead of the truck where a row appears. Scaled with the top speed so
 * a row is always ~2.4 s of warning, not less as the world got faster.
 */
export const SPAWN_Z = 240
/** Metres behind the truck where entities are dropped. */
export const CULL_Z = -25
/** Seconds of headway between rows at the start of a run. */
export const HEADWAY_START = 1.55
/** Seconds of headway between rows at full difficulty. */
export const HEADWAY_MIN = 0.72
/** Seconds of survival after which headway has fully tightened. */
export const HEADWAY_RAMP_TIME = 55
/** Chance a row holds two obstacles instead of one, at full difficulty. */
export const DOUBLE_OBSTACLE_MAX_CHANCE = 0.55
/** Seconds before the first row of a run — a moment to read the controls. */
export const GRACE_DISTANCE = 70
/** Chance that a free lane in a row carries an order. */
export const ORDER_CHANCE = 0.42
/** Chance that a free lane in a row carries fuel. */
export const FUEL_CHANCE = 0.14
/** Seconds between possible power-up spawns. */
export const POWERUP_COOLDOWN = 17
/** Chance of a power-up once the cooldown elapsed (rolled per row). */
export const POWERUP_CHANCE = 0.42
/** Share of power-up spawns that are Bobi rather than Sokar. */
export const BOBI_SHARE = 0.6

/* -------------------------------------------------------------- scoring --- */

/** Points per metre travelled. */
export const POINTS_PER_METRE = 1
export const POINTS_ORDER = 150
export const POINTS_FUEL = 60
export const POINTS_NEAR_MISS = 90
export const POINTS_AUTOPILOT_DODGE = 120
export const POINTS_SOKAR_CLEAR = 80
/** Combo multiplier = 1 + level * COMBO_STEP. */
export const COMBO_STEP = 0.5
export const MAX_COMBO = 6
/** Seconds of no fuel before the combo drops one level. */
export const COMBO_TIMEOUT = 9

/* ------------------------------------------------------------ power-ups --- */

/** Seconds of Bobi autopilot. */
export const AUTOPILOT_TIME = 5
/** Seconds of Sokar double points. */
export const DOUBLE_POINTS_TIME = 8
/** Metres ahead cleared when Sokar is picked up. */
export const SOKAR_CLEAR_RANGE = 150

/* ----------------------------------------------------------- near misses -- */

/** Seconds after a lane change during which a passed obstacle counts as a near miss. */
export const NEAR_MISS_WINDOW = 0.45

/* -------------------------------------------------------------- physics --- */

/** Fixed simulation step in seconds. The loop accumulates real time into these. */
export const FIXED_STEP = 1 / 120
/** Never simulate more than this much real time in one frame (tab wake-up guard). */
export const MAX_FRAME_TIME = 0.25

/* --------------------------------------------------------------- camera --- */

/**
 * Telephoto chase camera. A long lens far behind the rig keeps distant
 * obstacles large relative to the truck, so lanes stay ~40 px apart two
 * seconds out instead of collapsing into the horizon.
 */
/** Camera distance behind the truck's rear doors, metres. */
export const CAM_BACK = 55
/** Camera height above the road, metres. */
export const CAM_HEIGHT = 12
/** Focal length in "screen heights". */
export const FOCAL = 1.9
/** Fraction of canvas height where the horizon sits. */
export const HORIZON = 0.26

/* -------------------------------------------------------------- visuals --- */

export const OBSTACLE_VISUALS: Record<ObstacleKind, EntityVisual> = {
  itd: {
    halfWidth: 1.5,
    depth: 4.5,
    height: 3.2,
    color: '#ff4d6d',
    glow: 'rgba(255,77,109,0.55)',
    glyph: 'ITD',
    i18n: 'itd',
  },
  jam: {
    halfWidth: 1.55,
    depth: 6.5,
    height: 3.4,
    color: '#ff9f1c',
    glow: 'rgba(255,159,28,0.5)',
    glyph: '≡',
    i18n: 'jam',
  },
  closedExit: {
    halfWidth: 1.6,
    depth: 2.6,
    height: 2.6,
    color: '#f1f5f9',
    glow: 'rgba(241,245,249,0.4)',
    glyph: '⛔',
    i18n: 'closedExit',
  },
  noCmr: {
    halfWidth: 1.4,
    depth: 3,
    height: 3,
    color: '#a78bfa',
    glow: 'rgba(167,139,250,0.5)',
    glyph: 'CMR',
    i18n: 'noCmr',
  },
  badPayer: {
    halfWidth: 1.45,
    depth: 3.2,
    height: 3.1,
    color: '#ff3ea5',
    glow: 'rgba(255,62,165,0.5)',
    glyph: 'PLN',
    i18n: 'badPayer',
  },
}

export const PICKUP_VISUALS: Record<PickupKind, EntityVisual> = {
  order: {
    halfWidth: 0.85,
    depth: 1.6,
    height: 1.6,
    color: '#3b82f6',
    glow: 'rgba(59,130,246,0.6)',
    glyph: '□',
    i18n: 'order',
  },
  fuel: {
    halfWidth: 0.8,
    depth: 1.5,
    height: 1.8,
    color: '#2ecc71',
    glow: 'rgba(46,204,113,0.6)',
    glyph: '⛽',
    i18n: 'fuel',
  },
}

export const POWERUP_VISUALS: Record<PowerupKind, EntityVisual> = {
  bobi: {
    halfWidth: 1.05,
    depth: 2,
    height: 2.2,
    color: '#60a5fa',
    glow: 'rgba(96,165,250,0.85)',
    glyph: 'B',
    i18n: 'bobi',
  },
  sokar: {
    halfWidth: 1.05,
    depth: 2,
    height: 2.2,
    color: '#22d3ee',
    glow: 'rgba(34,211,238,0.85)',
    glyph: 'S',
    i18n: 'sokar',
  },
}

export const OBSTACLE_KINDS = Object.keys(OBSTACLE_VISUALS) as ObstacleKind[]

/** Look-up covering every entity kind. */
export const VISUALS: Record<string, EntityVisual> = {
  ...OBSTACLE_VISUALS,
  ...PICKUP_VISUALS,
  ...POWERUP_VISUALS,
}

/* ----------------------------------------------------------------- misc --- */

/** Brand palette, mirrored from onitsoft-web `src/views/Bobi/bobi.scss`. */
export const PALETTE = {
  bg: '#001018',
  bg2: '#00182a',
  ink: '#ffffff',
  accent: '#3b82f6',
  accent2: '#60a5fa',
  blue: '#2563eb',
  cyan: '#22d3ee',
  ok: '#2ecc71',
  warn: '#ff9f1c',
  danger: '#ff4d6d',
} as const
